import { useEffect } from 'react'
import { useForm, type UseFormRegisterReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { Separator } from '@/components/shadcn/separator'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog'
import { notify } from '@/lib/notify'
import {
  usePostApiCompaniesCurrentSubcompanies,
  usePatchApiCompaniesCurrentSubcompaniesSubCompanyId,
  getGetApiCompaniesCurrentSubcompaniesQueryKey,
} from '@/api/generated/company-management-user/company-management-user'
import type { SubCompanyModel } from '@/api/model/subCompanyModel'
import type { CreateSubCompanyModel } from '@/api/model/createSubCompanyModel'

type FormValues = {
  name: string
  phoneNumber: string
  taxId: string
  invoiceEmail: string
  street: string
  number: string
  extraAddressLine: string
  postalCode: string
  town: string
  country: string
}

const defaultValues: FormValues = {
  name: '', phoneNumber: '', taxId: '', invoiceEmail: '',
  street: '', number: '', extraAddressLine: '', postalCode: '', town: '', country: '',
}

interface Props {
  open: boolean
  onClose: () => void
  subCompany?: SubCompanyModel | null
}

function FormField({ id, label, required, placeholder, registration }: {
  id: string; label: string; required?: boolean; placeholder?: string; registration: UseFormRegisterReturn
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}{required && <span className="text-destructive ml-0.5">*</span>}</Label>
      <Input id={id} required={required} placeholder={placeholder} {...registration} />
    </div>
  )
}

export function SubCompanyDialog({ open, onClose, subCompany }: Props) {
  const { t } = useTranslation(['company', 'common'])
  const isEdit = !!subCompany
  const queryClient = useQueryClient()
  const { register, handleSubmit, reset } = useForm<FormValues>({ defaultValues })

  // On open dialog fill data
  useEffect(() => {
    if (open) {
      reset(isEdit ? {
        name:             subCompany.name         ?? '',
        phoneNumber:      subCompany.phoneNumber   ?? '',
        taxId:            subCompany.taxId         ?? '',
        invoiceEmail:     subCompany.invoiceEmail  ?? '',
        street:           subCompany.address?.street           ?? '',
        number:           subCompany.address?.number           ?? '',
        extraAddressLine: subCompany.address?.extraAddressLine ?? '',
        postalCode:       subCompany.address?.postalCode       ?? '',
        town:             subCompany.address?.town             ?? '',
        country:          subCompany.address?.country          ?? '',
      } : defaultValues)
    }
  }, [open, subCompany])

  const invalidate = () => queryClient.invalidateQueries({
    queryKey: getGetApiCompaniesCurrentSubcompaniesQueryKey(),
  })

  const close = () => {
    reset(defaultValues)
    onClose()
  }

  const { mutate: createSub, isPending: isCreating } = usePostApiCompaniesCurrentSubcompanies({
    mutation: {
      onSuccess: () => {
        notify.success(t('toasts.subCompanyCreated'))
        close()
        invalidate()
      },
      onError: (error) => notify.apiError(error, t('toasts.createError')),
    },
  })

  const { mutate: updateSub, isPending: isUpdating } = usePatchApiCompaniesCurrentSubcompaniesSubCompanyId({
    mutation: {
      onSuccess: () => {
        notify.success(t('toasts.subCompanyUpdated'))
        close()
        invalidate()
      },
      onError: (error) => notify.apiError(error, t('toasts.updateError')),
    },
  })

  const isPending = isCreating || isUpdating

  const onValid = (values: FormValues) => {
    const payload: CreateSubCompanyModel = {
      name:         values.name         || null,
      phoneNumber:  values.phoneNumber  || null,
      taxId:        values.taxId        || null,
      invoiceEmail: values.invoiceEmail || null,
      address: {
        street:           values.street           || null,
        number:           values.number           || null,
        extraAddressLine: values.extraAddressLine || null,
        postalCode:       values.postalCode       || null,
        town:             values.town             || null,
        country:          values.country          || null,
      },
    }
    if (isEdit) {
      updateSub({ subCompanyId: subCompany!.id, data: payload })
    } else {
      createSub({ data: payload })
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('subCompanyDialog.title')}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? t('subCompanyDialog.descriptionEdit')
              : t('subCompanyDialog.descriptionCreate')}
          </DialogDescription>
        </DialogHeader>

        <form id="subcompany-form" onSubmit={handleSubmit(onValid)} className="space-y-6 py-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{t('subCompanyDialog.sectionCompany')}</p>
            <div className="grid grid-cols-2 gap-4">
              <FormField id="name" label={t('subCompanyDialog.fields.name')} required placeholder={t('subCompanyDialog.placeholders.name')} registration={register('name')} />
              <FormField id="phoneNumber" label={t('subCompanyDialog.fields.phoneNumber')} required placeholder={t('subCompanyDialog.placeholders.phoneNumber')} registration={register('phoneNumber')} />
              <FormField id="taxId" label={t('subCompanyDialog.fields.taxId')} required placeholder={t('subCompanyDialog.placeholders.taxId')} registration={register('taxId')} />
              <FormField id="invoiceEmail" label={t('subCompanyDialog.fields.invoiceEmail')} required placeholder={t('subCompanyDialog.placeholders.invoiceEmail')} registration={register('invoiceEmail')} />
            </div>
          </div>

          <Separator />

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{t('subCompanyDialog.sectionAddress')}</p>
            <div className="grid grid-cols-2 gap-4">
              <FormField id="street" label={t('subCompanyDialog.fields.street')} required placeholder={t('subCompanyDialog.placeholders.street')} registration={register('street')} />
              <FormField id="number" label={t('subCompanyDialog.fields.number')} required placeholder={t('subCompanyDialog.placeholders.number')} registration={register('number')} />
              <FormField id="extraAddressLine" label={t('subCompanyDialog.fields.extraAddressLine')} placeholder={t('subCompanyDialog.placeholders.extraAddressLine')} registration={register('extraAddressLine')} />
              <FormField id="postalCode" label={t('subCompanyDialog.fields.postalCode')} required placeholder={t('subCompanyDialog.placeholders.postalCode')} registration={register('postalCode')} />
              <FormField id="town" label={t('subCompanyDialog.fields.town')} required placeholder={t('subCompanyDialog.placeholders.town')} registration={register('town')} />
              <FormField id="country" label={t('subCompanyDialog.fields.country')} required placeholder={t('subCompanyDialog.placeholders.country')} registration={register('country')} />
            </div>
          </div>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={close} disabled={isPending}>{t('buttons.cancel', { ns: 'common' })}</Button>
          <Button type="submit" form="subcompany-form" disabled={isPending}>
            {isPending ? t('buttons.saving', { ns: 'common' }) : t('buttons.save', { ns: 'common' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
